/**
 * Central query key factory.
 *
 * One rule, applied without exception: **every** key that describes tenant-owned
 * data starts with `['tenant', <tenantId>]`. That single rule gives the shell two
 * guarantees:
 *  - switching tenant produces different keys, so stale tenant data is never
 *    rendered under the new tenant
 *  - `queryClient.removeQueries({ queryKey: ['tenant', previousId] })` evicts
 *    exactly the previous tenant's cache without touching the rest
 *
 * There is deliberately no `'none'`/`null` placeholder tenant in any key. An
 * earlier revision keyed un-scoped queries as `['tenant', 'none', ...]`, which
 * meant the ids of two different tenants could collide with a placeholder bucket
 * and, worse, made it possible to cache business data outside a tenant scope at
 * all. Queries now stay disabled until a real tenant id exists.
 */

/** Placeholder topic id used by the dashboard "latest content" scope. */
export type TenantScope = string;

export const queryKeys = {
  /** Prefix for every tenant-owned entry; used to evict one tenant wholesale. */
  tenantScope: (tenantId: TenantScope) => ['tenant', tenantId] as const,

  analysis: (tenantId: TenantScope, ...scope: readonly unknown[]) =>
    [...queryKeys.tenantScope(tenantId), 'analysis', ...scope] as const,

  // The tenant list itself is *not* tenant-owned: it is what tells the client
  // which tenants exist, so it cannot live under a tenant prefix.
  tenants: () => ['tenants'] as const,

  topics: (tenantId: TenantScope, limit: number) =>
    [...queryKeys.tenantScope(tenantId), 'topics', { limit }] as const,
  crawlerSources: (tenantId: TenantScope) =>
    [...queryKeys.tenantScope(tenantId), 'crawler-sources'] as const,
  crawlerProxies: (tenantId: TenantScope) =>
    [...queryKeys.tenantScope(tenantId), 'crawler-proxies'] as const,
  discoverySites: (tenantId: TenantScope) =>
    [...queryKeys.tenantScope(tenantId), 'discovery-sites'] as const,
  topicRule: (tenantId: TenantScope, id: string) =>
    [...queryKeys.tenantScope(tenantId), 'topic-rule', id] as const,
  topicMatches: (tenantId: TenantScope, id: string) =>
    [...queryKeys.tenantScope(tenantId), 'topic-matches', id] as const,
  contents: (tenantId: TenantScope, limit: number) =>
    [...queryKeys.tenantScope(tenantId), 'contents', { limit }] as const,
  contentRevision: (tenantId: TenantScope, id: string) =>
    [...queryKeys.tenantScope(tenantId), 'content-revision', id] as const,
  deletionAssessment: (tenantId: TenantScope, id: string) =>
    [...queryKeys.tenantScope(tenantId), 'deletion-assessment', id] as const,
  deletionRequest: (tenantId: TenantScope, id: string) =>
    [...queryKeys.tenantScope(tenantId), 'deletion-request', id] as const,
  deletionRequests: (tenantId: TenantScope) =>
    [...queryKeys.tenantScope(tenantId), 'deletion-requests', { limit: 20 }] as const,
  alerts: (tenantId: TenantScope, limit: number) =>
    [...queryKeys.tenantScope(tenantId), 'alerts', { limit }] as const,
  alertDetail: (tenantId: TenantScope, userId: string, id: string) =>
    [...queryKeys.tenantScope(tenantId), 'alert-detail', userId, id] as const,
  alertExplanation: (tenantId: TenantScope, id: string) =>
    [...queryKeys.tenantScope(tenantId), 'alert-explanation', id] as const,
  alertRules: (tenantId: TenantScope) =>
    [...queryKeys.tenantScope(tenantId), 'alert-rules'] as const,

  /** 多模态演示: runs and the history list, invalidated together after a change. */
  multimodal: (tenantId: TenantScope, ...scope: readonly unknown[]) =>
    [...queryKeys.tenantScope(tenantId), 'multimodal', ...scope] as const,

  /** 舆情研判: every intel entry of one tenant, so a refresh can invalidate them together. */
  intel: (tenantId: TenantScope, ...scope: readonly unknown[]) =>
    [...queryKeys.tenantScope(tenantId), 'intel', ...scope] as const,
  intelStories: (tenantId: TenantScope, topicId: string, status: string) =>
    [...queryKeys.intel(tenantId, 'stories', topicId), { status }] as const,
  intelStory: (tenantId: TenantScope, storyId: string) =>
    [...queryKeys.intel(tenantId, 'story', storyId)] as const,
  intelTopicHeat: (tenantId: TenantScope, topicId: string, hours: number) =>
    [...queryKeys.intel(tenantId, 'topic-heat', topicId), { hours }] as const,
  intelHeatFormula: (tenantId: TenantScope) =>
    [...queryKeys.intel(tenantId, 'heat-formula')] as const,
  intelCalibration: (tenantId: TenantScope, topicId: string) =>
    [...queryKeys.intel(tenantId, 'calibration', topicId)] as const,
  intelCoordination: (tenantId: TenantScope, topicId: string, status: string) =>
    [...queryKeys.intel(tenantId, 'coordination', topicId), { status }] as const,
  intelSandboxRun: (tenantId: TenantScope, runId: string) =>
    [...queryKeys.intel(tenantId, 'sandbox', runId)] as const,
  jobs: (tenantId: TenantScope, limit: number) =>
    [...queryKeys.tenantScope(tenantId), 'jobs', { limit }] as const,
  reports: (tenantId: TenantScope, limit: number) =>
    [...queryKeys.tenantScope(tenantId), 'reports', { limit }] as const,
  reportDetail: (tenantId: TenantScope, id: string) =>
    [...queryKeys.tenantScope(tenantId), 'report-detail', id] as const,
  reportDocument: (tenantId: TenantScope, id: string, versionId: string) =>
    [...queryKeys.tenantScope(tenantId), 'report-document', id, versionId] as const,
  reportExportStatus: (tenantId: TenantScope, runId: string, versionId: string) =>
    [...queryKeys.tenantScope(tenantId), 'report-export-status', runId, versionId] as const,
  investigations: (tenantId: TenantScope, limit: number) =>
    [...queryKeys.tenantScope(tenantId), 'investigations', { limit }] as const,
};

export const DEFAULT_PAGE_SIZE = 50;

/** Prefix of every tenant-owned cache entry, across all tenants. */
export const TENANT_ROOT = 'tenant';

/** Prefix used to drop all cached business data for one tenant. */
export function tenantScopePrefix(tenantId: TenantScope): readonly unknown[] {
  return queryKeys.tenantScope(tenantId);
}

/**
 * Every query key that must be dropped when a session ends or a tenant changes.
 *
 * Sign-out has to clear tenant business data as well as the selected scope: leaving
 * tenant A's contents, alerts and job rows in the query cache would show them to
 * whoever signs in next on the same tab, before their own request resolves. Tenant
 * *selection* is a preference kept deliberately (it is not a credential); cached
 * rows are not.
 */
export function sensitiveQueryKeyPrefixes(): readonly (readonly unknown[])[] {
  return [
    [TENANT_ROOT],
    ['platform'],
    ['billing'],
    ['support'],
    ['public-sources'],
    ['public-source-requests'],
    ['operator-sources'],
    ['operator-query-requests'],
    ['content-sentiment'],
  ];
}
